import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { X } from "lucide-react";
import type { ProcedureReturnType } from "spacetimedb";
import type { ReactElement, RefAttributes } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/web/components/primitives/command";
import { Spinner } from "@/web/components/primitives/spinner";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import { cn } from "@/web/lib/styling";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";

export type AllNominationsRow = ProcedureReturnType<typeof procedures.searchNominations>[number];

type AnyControllerField = Record<string, unknown>;

let gapiInitialized = false;
let gapiInitPromise: Promise<void> | null = null;

async function ensureGapiInitialized() {
  if (gapiInitialized) return;
  if (!gapiInitPromise) {
    setOptions({ key: env.VITE_GAPI_PLACES_KEY, v: "weekly" });
    gapiInitPromise = importLibrary("places").then(() => {
      gapiInitialized = true;
    });
  }
  await gapiInitPromise;
}

function useGapiPlacesClient() {
  const [isInitialized, setIsInitialized] = useState(gapiInitialized);

  useEffect(() => {
    if (isInitialized) return;
    ensureGapiInitialized()
      .then(() => setIsInitialized(true))
      .catch(ErrorHandler.default);
  }, [isInitialized]);

  const locationBiasQuery = useCallback(
    async (query: string, sessionToken: google.maps.places.AutocompleteSessionToken) => {
      if (!isInitialized) return undefined;
      const { AutocompleteSuggestion } = google.maps.places;
      return await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken,
      });
    },
    [isInitialized],
  );

  const Predictions = useMemo(() => ({ locationBiasQuery }), [locationBiasQuery]);

  return { Predictions };
}

function useGapiPlacesSearch({
  debounceMs = 1_000,
  minQueryLength = 3,
}: {
  debounceMs?: number;
  minQueryLength?: number;
}) {
  const { Predictions } = useGapiPlacesClient();
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (query.length === 0) {
      sessionRef.current = null;
      setPredictions((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    if (query.length < minQueryLength) return;
    setIsFetching(true);
    const timeout = setTimeout(() => {
      if (!sessionRef.current) {
        sessionRef.current = new google.maps.places.AutocompleteSessionToken();
      }
      const token = sessionRef.current;
      Predictions.locationBiasQuery(query, token)
        .then((res) => {
          if (!res) return;
          setPredictions(
            res.suggestions
              .map((s) => s.placePrediction)
              .filter((p): p is google.maps.places.PlacePrediction => p != null),
          );
        })
        .catch(ErrorHandler.default)
        .finally(() => setIsFetching(false));
    }, debounceMs);
    return () => clearTimeout(timeout);
  }, [query, debounceMs, minQueryLength, Predictions]);

  return { query, setQuery, predictions, isFetching, sessionRef };
}

function formatLocation(address: AllNominationsRow["canonicalAddress"]): string | undefined {
  if (!address) return undefined;
  const parts = [address.city, address.state].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(", ") : undefined;
}

function NominationMatchesGroup({
  query,
  disabled,
  onSelect,
}: {
  query: string;
  disabled: boolean;
  onSelect: (nomination: AllNominationsRow) => void;
}) {
  const { mutateAsync: search } = useProcedureMutation(procedures.searchNominations);
  const [matches, setMatches] = useState<AllNominationsRow[]>([]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setMatches([]);
      return;
    }
    search({ query: trimmed }).then(setMatches).catch(ErrorHandler.default);
  }, [query, search]);

  if (matches.length === 0) return null;

  return (
    <CommandGroup heading="Nominated businesses" className="p-1">
      {matches.map((nomination) => {
        const location = formatLocation(nomination.canonicalAddress);
        return (
          <CommandItem
            key={nomination.id}
            onSelect={() => {
              if (disabled) return;
              onSelect(nomination);
            }}
          >
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-1 text-muted-foreground">
                <span className="truncate font-medium text-primary">
                  {nomination.name ?? "Unnamed business"}
                </span>
                {location && <span className="truncate text-xs">{location}</span>}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {nomination.totalInterested} interested
              </span>
            </div>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}

type GapiSearchBarProps = {
  onSelectPrediction: (prediction: google.maps.places.PlacePrediction) => void | Promise<void>;
  onSelectNomination?: (nomination: AllNominationsRow) => void | Promise<void>;
  onManualEntryRequested?: (query: string) => void;
  field?: AnyControllerField;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  getPlaceAnnotation?: (placeId: string) => string | undefined;
  /** Display label of the currently selected item. When set, the bar shows this
   *  value (with a clear affordance) instead of the autocomplete input, so a
   *  picked business/address stays visible instead of the bar emptying. */
  value?: string;
  /** Clear the current selection so the bar returns to search mode. */
  onClear?: () => void;
};

export type GapiPlacesSearchBarRef = {
  focusInput: () => void;
};

/**
 * Google Places autocomplete (new) — predictions only, no nomination list.
 * Session token is reused for one search→select cycle per Google’s billing model.
 */
export const GapiPlacesSearchBar = forwardRef(function GapiPlacesSearchBarInner(
  {
    onSelectPrediction,
    onSelectNomination,
    onManualEntryRequested,
    field: _field,
    disabled = false,
    placeholder = "Search for a business...",
    className,
    getPlaceAnnotation,
    value,
    onClear,
  }: GapiSearchBarProps,
  ref: RefAttributes<GapiPlacesSearchBarRef>["ref"],
) {
  const { query, setQuery, predictions, isFetching, sessionRef } = useGapiPlacesSearch({});
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ focusInput: () => inputRef.current?.focus() }));

  const shouldShowList = query.length > 0;

  // When the parent supplies a selected-value label, the bar shows that value
  // (with a clear affordance) instead of the autocomplete input. This keeps a
  // picked business/address visible rather than leaving the bar empty.
  if (value !== undefined && value !== "") {
    return (
      <div
        className={cn(
          "flex h-input w-full min-w-0 items-center gap-2 rounded-none border bg-input px-3 py-2 text-base text-input-foreground md:text-sm",
          "outline-none placeholder:text-input-foreground/40",
          className,
        )}
      >
        <span className="min-w-0 flex-1 truncate">{value}</span>
        {onClear && !disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => {
              setQuery("");
              onClear();
            }}
            className="shrink-0 rounded-none p-0.5 text-input-foreground/50 hover:text-input-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <Command
      shouldFilter={false}
      className={cn(
        "relative isolate w-full overflow-visible before:pointer-events-none",
        // Lift an open dropdown above following siblings (e.g. the second admin
        // search bar) so results are never overlaid by the next field.
        shouldShowList ? "z-50" : "z-0",
        className,
      )}
    >
      <CommandInput
        ref={inputRef}
        value={query}
        onValueChange={(value) => {
          if (!disabled) setQuery(value);
        }}
        disabled={disabled}
        className="relative z-20"
        placeholder={placeholder}
      />
      {shouldShowList && (
        <CommandList className="absolute top-[calc(100%+4px)] left-0 z-20 max-h-90 w-full bg-input shadow-md">
          {isFetching && (
            <CommandEmpty>
              <Spinner className="text-muted-foreground" />
            </CommandEmpty>
          )}
          {onSelectNomination && (
            <NominationMatchesGroup
              query={query}
              disabled={disabled}
              onSelect={(nomination) => {
                void (async () => {
                  try {
                    await onSelectNomination(nomination);
                  } finally {
                    sessionRef.current = null;
                    setQuery("");
                  }
                })();
              }}
            />
          )}
          {predictions.length > 0 && (
            <CommandGroup className="p-1">
              {predictions.map((prediction) => {
                const annotation = getPlaceAnnotation?.(prediction.placeId);
                return (
                  <CommandItem
                    key={prediction.placeId}
                    onSelect={() => {
                      void (async () => {
                        if (disabled) return;
                        try {
                          await onSelectPrediction(prediction);
                        } finally {
                          sessionRef.current = null;
                          setQuery("");
                        }
                      })();
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-2 text-muted-foreground">
                      <div className="flex min-w-0 flex-col gap-1">
                        <span className="truncate font-medium text-primary">
                          {prediction.mainText?.text}
                        </span>
                        <span className="truncate text-xs">{prediction.secondaryText?.text}</span>
                      </div>
                      {annotation ? (
                        <span className="shrink-0 border border-withus-dark-blue/20 px-2 py-1 text-[11px] font-medium text-withus-dark-blue">
                          {annotation}
                        </span>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}
          {!isFetching && onManualEntryRequested && (
            <CommandGroup className="p-1">
              <CommandItem
                onSelect={() => {
                  if (disabled) return;
                  onManualEntryRequested(query);
                }}
              >
                <div className="line-clamp-1 text-muted-foreground">Add "{query}"</div>
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      )}
    </Command>
  );
}) as (props: GapiSearchBarProps & RefAttributes<GapiPlacesSearchBarRef>) => ReactElement;
